import type { JanusJS } from 'lib/janus';

export type IceServerListProblem =
  'not_array' | 'empty' | 'malformed_entry' | 'missing_turn_credential';

const ICE_URL = /^(stuns?|turns?):\S+$/i;
const TURN_URL = /^turns?:/i;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const urlListOf = (urls: string | string[] | undefined): string[] => {
  if (!urls) {
    return [];
  }

  return Array.isArray(urls) ? urls : [urls];
};

export const hasTurnCredential = (username: unknown, credential: unknown): boolean =>
  isNonEmptyString(username) && isNonEmptyString(credential);

const entryProblem = (entry: unknown): IceServerListProblem | null => {
  if (typeof entry !== 'object' || entry === null) {
    return 'malformed_entry';
  }

  const { urls, username, credential } = entry as Record<string, unknown>;
  const urlList = typeof urls === 'string' ? [urls] : urls;

  if (
    !Array.isArray(urlList) ||
    urlList.length === 0 ||
    !urlList.every((url) => typeof url === 'string' && ICE_URL.test(url))
  ) {
    return 'malformed_entry';
  }

  // Firefox 151+ fails the whole PC on an empty TURN credential; Chrome and Safari accept it.
  if (urlList.some((url) => TURN_URL.test(url)) && !hasTurnCredential(username, credential)) {
    return 'missing_turn_credential';
  }

  return null;
};

export const iceServerListProblem = (value: unknown): IceServerListProblem | null => {
  if (!Array.isArray(value)) {
    return 'not_array';
  }

  if (value.length === 0) {
    return 'empty';
  }

  return value.map(entryProblem).find((problem) => problem !== null) ?? null;
};

// TURN REST API usernames carry the expiry in Unix seconds as `<expiry>:<user>` (coturn also
// reads `<user>:<expiry>`). A static username has no separator and never expires.
export const turnCredentialExpiryMs = (username: unknown): number | null => {
  if (typeof username !== 'string') {
    return null;
  }

  const separator = username.indexOf(':');

  if (separator < 0) {
    return null;
  }

  const head = username.slice(0, separator);
  const timestamp = /^\d+$/.test(head) ? head : username.slice(separator + 1);

  return /^\d+$/.test(timestamp) ? Number(timestamp) * 1000 : null;
};

export const turnCredentialRemainingSec = (
  iceServers: RTCIceServer[] | undefined,
  nowMs = Date.now()
): number | null => {
  const expiries = (iceServers ?? [])
    .filter((server) => urlListOf(server.urls).some((url) => TURN_URL.test(url)))
    .map((server) => turnCredentialExpiryMs(server.username))
    .filter((expiryMs): expiryMs is number => expiryMs !== null);

  return expiries.length > 0 ? Math.floor((Math.min(...expiries) - nowMs) / 1000) : null;
};

export const turnUnauthorizedErrors = (
  candidateErrors: JanusJS.IceCandidateError[] | undefined
): JanusJS.IceCandidateError[] =>
  (candidateErrors ?? []).filter(
    (error) => error.errorCode === 401 && TURN_URL.test(error.url ?? '')
  );

// The local-clock expiry is the only signal on engines that fire no icecandidateerror.
export const isTurnCredentialRefused = (
  candidateErrors: JanusJS.IceCandidateError[] | undefined,
  iceServers: RTCIceServer[] | undefined,
  nowMs = Date.now()
): boolean => {
  if (turnUnauthorizedErrors(candidateErrors).length > 0) {
    return true;
  }

  const remainingSec = turnCredentialRemainingSec(iceServers, nowMs);

  return remainingSec !== null && remainingSec <= 0;
};
